// Een shift: één werkdag van 4 minuten. Houdt de klok, aanbiedingen en lopende orders bij.
// Geen DOM of canvas: BezorgerGame roept accept/load/deliver aan en tekent de rest.
import {
  applyShock,
  createOffer,
  scoreDelivery,
  tickOrder,
  type CarryContext,
  type DeliveryResult,
  type Destination,
  type Order,
  OFFER_LIFETIME_SEC,
  reserveOrderIds,
  type Rng,
} from './orders';
import { productById } from './products';
import { activeClosures, advanceEvents, planEvents, weatherFor, type Closure, type EventNotice, type GameEvent, type ShiftWeather } from './events';
import type { RouteOptions } from './route';

/** Lengte van een shift in echte seconden. Eén seconde is één minuut op de klok. */
export const SHIFT_SECONDS = 240;
const START_MINUTE = 7 * 60;
export const MAX_OPEN_OFFERS = 3;
/** In de rustige eerste shift krijg je extra tijd en blijft het aanbod staan. */
export const CALM_DEADLINE_FACTOR = 1.7;

/** Doelbedragen per rang voor 1, 2 en 3 dagsterren. */
export const SHIFT_TARGETS: Record<number, [number, number, number]> = {
  1: [25, 45, 70],
  2: [40, 70, 100],
  3: [55, 90, 130],
};

export function clockLabel(t: number): string {
  const minutes = START_MINUTE + Math.min(SHIFT_SECONDS, Math.max(0, Math.floor(t)));
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

export interface ShiftOptions {
  rng: Rng;
  rank: number;
  districts: string[];
  destinations: Destination[];
  bakery: { x: number; y: number };
  capacity: number;
  /** Mag Sjors om deze klant racen? Standaard altijd; het spel geeft hier de eerlijkheidscontrole mee. */
  raceFair?: (dest: Destination) => boolean;
  /** Staat de ophaalbrug open of gaat hij open? Het spel geeft hier de stand van de wereld mee. */
  bridgeOpen?: () => boolean;
  /** Rustige eerste shift: één bestelling tegelijk, geen gebeurtenissen, geen Sjors, ruime tijd. */
  calm?: boolean;
}

export interface Delivered {
  order: Order;
  result: DeliveryResult;
}

export type AcceptRefusal = 'vol' | 'bruidstaart-alleen' | 'bestaat-niet';

export interface ShiftSummary {
  deliveries: number;
  earnings: number;
  /** Sterren verdiend met leveringen deze shift */
  stars: number;
  averageStars: number;
  missed: number;
  /** Orders die Sjors je afsnoepte */
  lost: number;
  raceWins: number;
  bestCombo: number;
  shiftStars: number;
  target: [number, number, number];
}

/** Kans dat een aanbod een race tegen Sjors is (als er geen race loopt). */
export const RACE_CHANCE = 0.22;
/** Bonus als je Sjors voor bent. */
export const RACE_BONUS = 3;

/** Een order zoals hij in de opslag staat: product en klant als id, de rest als getallen. */
export interface SavedOrder {
  id: string;
  productId: string;
  destId: string;
  customer: string;
  status: Order['status'];
  offerExpiresAt: number;
  deadlineSec: number;
  loadedAt: number | null;
  temp: number;
  integrity: number;
  expectedPay: number;
  race: boolean;
}

/** Alles wat een shift in uitvoering nodig heeft om later verder te gaan. */
export interface ShiftSnapshot {
  t: number;
  combo: number;
  bestCombo: number;
  earnings: number;
  stars: number;
  raceWins: number;
  nextOfferAt: number;
  brideAt: number | null;
  events: GameEvent[];
  orders: SavedOrder[];
  delivered: { orderId: string; result: DeliveryResult }[];
}

export class Shift {
  t = 0;
  orders: Order[] = [];
  delivered: Delivered[] = [];
  combo = 0;
  bestCombo = 0;
  earnings = 0;
  stars = 0;
  raceWins = 0;
  ended = false;
  private nextOfferAt = 0.5;
  private brideAt: number | null;
  private opts: ShiftOptions;
  /** Rustige stand (geleide eerste minuut). Kan midden in de shift uitgezet worden. */
  calm: boolean;
  /** Regenbui, mist en marktdag van deze shift */
  readonly events: GameEvent[];
  private notices: EventNotice[] = [];

  constructor(opts: ShiftOptions) {
    this.opts = opts;
    this.calm = !!opts.calm;
    this.events = this.calm ? [] : planEvents(opts.rng, opts.rank);
    this.brideAt = !this.calm && opts.rank >= 2 ? 60 + opts.rng() * 90 : null;
  }

  get capacity() {
    return this.opts.capacity;
  }
  get offers() {
    return this.orders.filter((o) => o.status === 'aangeboden');
  }
  get accepted() {
    return this.orders.filter((o) => o.status === 'geaccepteerd');
  }
  get loaded() {
    return this.orders.filter((o) => o.status === 'ingeladen');
  }
  get remaining() {
    return Math.max(0, SHIFT_SECONDS - this.t);
  }
  /** Het weer dat bij de gebeurtenissen hoort */
  get weather(): ShiftWeather {
    return weatherFor(this.events);
  }
  /** De straten die nu dicht zijn */
  get closures(): Closure[] {
    return activeClosures(this.events);
  }
  /** Afsluitingen en brugstand voor de routeplanner */
  get routeOptions(): RouteOptions {
    return { blocked: this.closures, bridgeOpen: this.opts.bridgeOpen?.() ?? false };
  }
  /** Legt de stand van de shift vast, om hem later te kunnen hervatten. */
  snapshot(): ShiftSnapshot {
    return {
      t: this.t,
      combo: this.combo,
      bestCombo: this.bestCombo,
      earnings: this.earnings,
      stars: this.stars,
      raceWins: this.raceWins,
      nextOfferAt: this.nextOfferAt,
      brideAt: this.brideAt,
      events: JSON.parse(JSON.stringify(this.events)),
      orders: this.orders.map((o) => ({
        id: o.id,
        productId: o.product.id,
        destId: o.dest.id,
        customer: o.customer,
        status: o.status,
        offerExpiresAt: o.offerExpiresAt,
        deadlineSec: o.deadlineSec,
        loadedAt: o.loadedAt,
        temp: o.temp,
        integrity: o.integrity,
        expectedPay: o.expectedPay,
        race: !!o.race,
      })),
      delivered: this.delivered.map((d) => ({ orderId: d.order.id, result: d.result })),
    };
  }

  /** Zet een eerder vastgelegde stand terug. Orders met een onbekend product of klant vallen weg. */
  restore(snap: ShiftSnapshot) {
    this.t = snap.t;
    this.combo = snap.combo;
    this.bestCombo = snap.bestCombo;
    this.earnings = snap.earnings;
    this.stars = snap.stars;
    this.raceWins = snap.raceWins;
    this.nextOfferAt = snap.nextOfferAt;
    this.brideAt = snap.brideAt;
    this.events.splice(0, this.events.length, ...snap.events);
    this.orders = [];
    for (const s of snap.orders) {
      const product = productById(s.productId);
      const dest = this.opts.destinations.find((d) => d.id === s.destId);
      if (!product || !dest) continue;
      this.orders.push({
        id: s.id,
        product,
        dest,
        customer: s.customer,
        status: s.status,
        offerExpiresAt: s.offerExpiresAt,
        deadlineSec: s.deadlineSec,
        loadedAt: s.loadedAt,
        temp: s.temp,
        integrity: s.integrity,
        expectedPay: s.expectedPay,
        race: s.race || undefined,
      });
    }
    for (const o of this.orders) reserveOrderIds(Number(o.id.replace(/D/g, '')) || 0);
    this.delivered = [];
    for (const d of snap.delivered) {
      const order = this.orders.find((o) => o.id === d.orderId);
      if (order) this.delivered.push({ order, result: d.result });
    }
  }

  /** Haalt de meldingen op die sinds de vorige keer zijn ontstaan (waarschuwing, start, einde) */
  takeNotices(): EventNotice[] {
    const n = this.notices;
    this.notices = [];
    return n;
  }
  get isOver() {
    return this.t >= SHIFT_SECONDS;
  }

  /** Bezette plekken in de bak: ingeladen plus geaccepteerd. Een bruidstaart vult alles. */
  get usedSlots() {
    const active = [...this.accepted, ...this.loaded];
    if (active.some((o) => o.product.fillsBox)) return this.capacity;
    return active.length;
  }

  /** Geeft nieuwe aanbiedingen terug, zodat de interface ze kan melden. */
  update(dt: number): Order[] {
    if (this.ended) return [];
    this.t += dt;
    this.notices.push(...advanceEvents(this.events, this.t));
    // Rustige shift: het aanbod blijft staan tot je beslist
    for (const o of this.offers) {
      if (this.calm) o.offerExpiresAt = this.t + OFFER_LIFETIME_SEC;
      else if (this.t >= o.offerExpiresAt) o.status = 'verlopen';
    }

    const fresh: Order[] = [];
    if (this.brideAt !== null && this.t >= this.brideAt) {
      this.brideAt = null;
      const bride = productById('bruidstaart');
      const offer = bride && this.makeOffer(bride);
      if (offer) fresh.push(offer);
    }

    // Niets te doen? Dan komt er snel iets binnen.
    const idle = this.offers.length === 0 && this.usedSlots === 0;
    if (idle && this.nextOfferAt > this.t + 2) this.nextOfferAt = this.t + 2;

    if (this.t >= this.nextOfferAt && this.remaining > 20) {
      if (this.offers.length < (this.calm ? 1 : MAX_OPEN_OFFERS)) {
        const offer = this.makeOffer();
        if (offer) {
          // Af en toe wil Sjors van Knus Expres dezelfde klant (niet bij de eerste aanbiedingen).
          const raceRunning = this.orders.some((o) => o.race && ['aangeboden', 'geaccepteerd', 'ingeladen'].includes(o.status));
          const fair = this.opts.raceFair ? this.opts.raceFair(offer.dest) : true;
          if (!this.calm && this.t > 15 && !raceRunning && fair && !offer.product.fillsBox && this.opts.rng() < RACE_CHANCE) offer.race = true;
          fresh.push(offer);
        }
        // Aan het begin twee aanbiedingen kort na elkaar, daarna rustiger.
        this.nextOfferAt = this.t + (this.t < 3 ? 1.5 : 8 + this.opts.rng() * 6);
      } else {
        this.nextOfferAt = this.t + 3;
      }
    }
    return fresh;
  }

  private makeOffer(product?: ReturnType<typeof productById>): Order | null {
    const busy = new Set(
      this.orders.filter((o) => o.status === 'aangeboden' || o.status === 'geaccepteerd' || o.status === 'ingeladen').map((o) => o.dest.id),
    );
    const offer = createOffer(
      {
        rng: this.opts.rng,
        rank: this.opts.rank,
        districts: this.opts.districts,
        destinations: this.opts.destinations,
        bakery: this.opts.bakery,
        routeOptions: this.routeOptions,
        now: this.t,
        busyDestinationIds: busy,
      },
      product,
    );
    if (offer && this.calm) offer.deadlineSec = Math.round(offer.deadlineSec * CALM_DEADLINE_FACTOR);
    if (offer) this.orders.push(offer);
    return offer;
  }

  canAccept(id: string): AcceptRefusal | null {
    const order = this.orders.find((o) => o.id === id && o.status === 'aangeboden');
    if (!order) return 'bestaat-niet';
    if (order.product.fillsBox) return this.usedSlots === 0 ? null : 'bruidstaart-alleen';
    return this.usedSlots < this.capacity ? null : 'vol';
  }

  accept(id: string): AcceptRefusal | null {
    const refusal = this.canAccept(id);
    if (refusal) return refusal;
    const order = this.orders.find((o) => o.id === id)!;
    order.status = 'geaccepteerd';
    return null;
  }

  decline(id: string) {
    const order = this.orders.find((o) => o.id === id && o.status === 'aangeboden');
    if (order) order.status = 'verlopen';
  }

  /** Laadt alle geaccepteerde orders in bij de bakkerij. Vanaf nu loopt hun deadline. */
  load(): Order[] {
    const toLoad = this.accepted;
    for (const o of toLoad) {
      o.status = 'ingeladen';
      o.loadedAt = this.t;
      o.temp = 100;
      o.integrity = 100;
    }
    return toLoad;
  }

  /** Levert de ingeladen order voor deze bestemming af. */
  deliver(destId: string): Delivered | null {
    const order = this.loaded.find((o) => o.dest.id === destId);
    if (!order) return null;
    const result = scoreDelivery(order, this.t, this.combo);
    order.status = 'bezorgd';
    if (result.stars >= 4) {
      this.combo += 1;
      this.bestCombo = Math.max(this.bestCombo, this.combo);
    } else {
      this.combo = 0;
    }
    this.earnings += result.total;
    this.stars += result.stars;
    if (order.race) {
      this.raceWins += 1;
      this.earnings += RACE_BONUS;
    }
    const delivered = { order, result };
    this.delivered.push(delivered);
    return delivered;
  }

  /** Sjors was eerder: de klant hoeft jouw bestelling niet meer. */
  lose(id: string): Order | null {
    const order = this.orders.find((o) => o.id === id && (o.status === 'geaccepteerd' || o.status === 'ingeladen'));
    if (!order) return null;
    order.status = 'verloren';
    return order;
  }

  /** Extra inkomsten of kosten tijdens de shift (munten op straat, boetes). */
  addEarnings(amount: number) {
    this.earnings += amount;
  }

  /** Verdeelt een schok over alle ingeladen orders. Geeft de schade per order terug. */
  shock(severity: number, ctx: CarryContext): { order: Order; damage: number }[] {
    return this.loaded.map((order) => ({ order, damage: applyShock(order, severity, ctx.box, ctx.vehicle) }));
  }

  tickCargo(dt: number, ctx: CarryContext) {
    for (const o of this.loaded) tickOrder(o, dt, ctx);
  }

  /** Sluit de shift af. Wat nog in de bak of klaar zit, telt als gemist. */
  end(): ShiftSummary {
    if (!this.ended) {
      for (const o of this.orders) {
        if (o.status === 'geaccepteerd' || o.status === 'ingeladen') o.status = 'mislukt';
        if (o.status === 'aangeboden') o.status = 'verlopen';
      }
      this.ended = true;
    }
    return this.summary();
  }

  summary(): ShiftSummary {
    const target = SHIFT_TARGETS[this.opts.rank] ?? SHIFT_TARGETS[3];
    const deliveries = this.delivered.length;
    const averageStars = deliveries ? this.delivered.reduce((s, d) => s + d.result.stars, 0) / deliveries : 0;
    const shiftStars = target.filter((goal) => this.earnings >= goal).length;
    return {
      deliveries,
      earnings: Math.round(this.earnings * 100) / 100,
      stars: this.stars,
      averageStars,
      missed: this.orders.filter((o) => o.status === 'mislukt').length,
      lost: this.orders.filter((o) => o.status === 'verloren').length,
      raceWins: this.raceWins,
      bestCombo: this.bestCombo,
      shiftStars,
      target,
    };
  }
}
